/**
 * What a tool actually returned — dug out of the four shapes it can arrive in.
 *
 * Ported from the `tool_end` branch of `bimgo-ai-web`'s `chatService`. This is
 * the messiest corner of the protocol, and it is messy for a reason: the payload
 * has been through LangChain's serialiser, then MCP's content envelope, and the
 * useful part is a JSON document embedded in a **text** field. Nothing upstream
 * normalises it, so the client unwraps:
 *
 *   1. **LangChain**: `{ lc: 1, type: "constructor", kwargs: {…} }` → `kwargs`,
 *      or `.content` when there are no kwargs.
 *   2. **MCP**: `{ content: "…json…" }`, or `{ content: [{ type: "text", text }] }`.
 *   3. Already-parsed objects, with `artifact` or with `result` / `execution`.
 *
 * And the embedded JSON does not start at character 0 — the tool prefixes prose —
 * so parsing begins at the **first `{`**.
 *
 * Two rules that matter more than the unwrapping:
 *
 *   - **An artifact only counts when `success === true`.** The artifact of a
 *     failed run describes code that did not do what it says; showing it as a
 *     result is how a user comes to believe a change landed when it did not.
 *   - **A step is failed only when the payload says so.** `success: false`,
 *     `Success: false` or `status: "error"` — never the mere absence of a flag,
 *     which is what most of the read-only tools return (`reportedFailure`, #109).
 *   - **A failure still has something to say.** When there is no `result`, the
 *     `errorMessage` becomes the output, so a failed step reads as a reason
 *     rather than as an empty box.
 *
 * Every branch here is a shape seen in production by the client this is ported
 * from; none is speculative, and the tests pin each one.
 */

export interface ExecutionResult {
  success: boolean;
  /** The tool's own output, stringified when it came back structured. */
  output?: string;
  /** Milliseconds, when the tool reported them. */
  duration?: number;
  /** The unstringified `result`, for anything that wants the real value. */
  rawResult?: unknown;
  /** A structured display payload (tables, previews) when the tool sent one. */
  display?: Record<string, unknown>;
}

export interface ToolArtifact extends Record<string, unknown> {
  success: true;
}

export interface ParsedToolOutput {
  artifact: ToolArtifact | null;
  execution: ExecutionResult | null;
}

const EMPTY: ParsedToolOutput = { artifact: null, execution: null };

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * The JSON document inside a text blob. Starts at the first `{` because the
 * tool prefixes prose, and returns null rather than throwing on anything that
 * is not JSON after all.
 */
function parseJsonFromText(text: string): Record<string, unknown> | null {
  const start = text.indexOf("{");
  if (start === -1) return null;
  try {
    const parsed: unknown = JSON.parse(text.slice(start));
    return isRecord(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

/**
 * The artifact proper, out of whatever wraps it.
 *
 * The tools' artifact channel is an envelope — `{ artifact, execution, display,
 * success }` — and an agent that forwards the envelope whole (2.5 did, until
 * item BW) puts the code at `artifact.artifact.code`, one level below where
 * Save Skill looks. Peel that layer when it is there: the inner record is the
 * artifact if it carries the code; the outer one is when nothing is nested.
 */
function innerArtifact(artifact: Record<string, unknown>): Record<string, unknown> {
  const nested = artifact["artifact"];
  if (typeof artifact["code"] !== "string" && isRecord(nested)) return nested;
  return artifact;
}

/** Only a successful run has an artifact worth showing — see the header. */
function extractArtifact(parsed: Record<string, unknown>): ToolArtifact | null {
  if (parsed["success"] !== true) return null;
  const artifact = parsed["artifact"];
  if (!isRecord(artifact)) return null;
  return { ...innerArtifact(artifact), success: true };
}

/**
 * Did the payload **say** it failed?
 *
 * Only an explicit signal counts: `success: false` (the code-execution and
 * skill envelopes), `Success: false` (the plugin's analysis commands, passed
 * through as-is), or `status: "error"` (the docs and library tools). The
 * absence of a flag is not failure — `get_current_view_info` answers with a
 * bare `ViewInfo` object, `resolve_revit_docs_tool` with `{ items, coverage }`,
 * and neither carries `success` at all. Reading absence as failure drew a red
 * cross on every one of those steps after a correct result (#109); the stream
 * itself sends `tool_error` for a tool that threw, so the payload only has to
 * speak up when it *reported* a failure.
 */
function reportedFailure(parsed: Record<string, unknown>): boolean {
  return parsed["success"] === false || parsed["Success"] === false || parsed["status"] === "error";
}

function extractExecution(parsed: Record<string, unknown>): ExecutionResult {
  const success = !reportedFailure(parsed);
  const result = parsed["result"];
  // The reason, in whichever casing the tool used: the analysis commands say
  // `Message`, the docs tools `message`, the execution envelope `errorMessage`.
  const errorMessage = parsed["errorMessage"] ?? parsed["Message"] ?? parsed["message"];

  const output =
    result !== undefined && result !== null
      ? typeof result === "string"
        ? result
        : JSON.stringify(result, null, 2)
      : // A failure with no result still has a reason, and an empty box is worse
        // than the reason.
        !success && errorMessage
          ? String(errorMessage)
          : undefined;

  const execution = parsed["execution"];
  const duration = isRecord(execution) && typeof execution["durationMs"] === "number"
    ? (execution["durationMs"] as number)
    : undefined;

  const display = isRecord(parsed["display"]) ? (parsed["display"] as Record<string, unknown>) : undefined;

  const out: ExecutionResult = { success };
  if (output !== undefined) out.output = output;
  if (duration !== undefined) out.duration = duration;
  if (result !== undefined) out.rawResult = result;
  if (display !== undefined) out.display = display;
  return out;
}

/**
 * Peel LangChain's serialiser off, if it is there.
 *
 * Only `kwargs` needs unwrapping. The reference also special-cases a `.content`
 * on the envelope, but that is redundant here: leaving the object alone drops it
 * straight into the MCP `content` branch below, which does the same thing.
 * Mutating the fallback away changed no test, which is how it was found.
 */
function unwrapLangChain(output: unknown): unknown {
  if (!isRecord(output) || output["lc"] !== 1) return output;
  if (isRecord(output["kwargs"])) return output["kwargs"];
  return output;
}

export function parseToolOutput(output: unknown): ParsedToolOutput {
  let data = unwrapLangChain(output);

  // A bare string is the whole payload: the JSON is inside it.
  if (typeof data === "string") {
    const parsed = parseJsonFromText(data);
    return parsed ? { artifact: extractArtifact(parsed), execution: extractExecution(parsed) } : EMPTY;
  }

  if (!isRecord(data)) return EMPTY;

  // A ToolMessage's own artifact channel (`content_and_artifact`), beside its
  // text: the text carries the run's result, the envelope carries the
  // artifact. The text alone never mentions the artifact, so without this the
  // `tool_end` of a deliverable had a result and no code (item BW).
  const channel = isRecord(data["artifact"]) ? (data["artifact"] as Record<string, unknown>) : null;

  // MCP, content as a string.
  if (typeof data["content"] === "string") {
    const parsed = parseJsonFromText(data["content"] as string);
    if (!parsed) return channel ? { artifact: extractArtifact(channel), execution: extractExecution(channel) } : EMPTY;
    return { artifact: extractArtifact(parsed) ?? (channel ? extractArtifact(channel) : null), execution: extractExecution(parsed) };
  }

  // MCP, content as an array of parts.
  if (Array.isArray(data["content"])) {
    const parts = data["content"] as unknown[];
    const text = parts.find((c) => isRecord(c) && c["type"] === "text");
    const body = isRecord(text) && typeof text["text"] === "string" ? (text["text"] as string) : null;
    if (!body) return EMPTY;
    const parsed = parseJsonFromText(body);
    return parsed ? { artifact: extractArtifact(parsed), execution: extractExecution(parsed) } : EMPTY;
  }

  // Already parsed. The reference keys this on `artifact` or `result`/`execution`;
  // `success` and `display` are included here too, deliberately — an object that
  // states its own success *is* a tool-result envelope, and a tool whose whole
  // output is a display payload (a table, a preview) would otherwise render as
  // nothing at all.
  if (
    data["artifact"] !== undefined ||
    data["result"] !== undefined ||
    data["execution"] !== undefined ||
    data["success"] !== undefined ||
    data["display"] !== undefined
  ) {
    return { artifact: extractArtifact(data), execution: extractExecution(data) };
  }

  return EMPTY;
}

/**
 * The `tool_artifact` event, which the agent emits **before** `tool_end`.
 *
 * It exists because `send_code_to_revit`'s real result can be large enough to be
 * normalised out of the stream; this is the bounded copy that survives. Same
 * success rule as everywhere else.
 */
export function artifactFromEvent(data: Record<string, unknown>): ParsedToolOutput {
  if (data["success"] !== true || !isRecord(data["artifact"])) return EMPTY;
  const execution = isRecord(data["execution"]) ? data["execution"] : null;
  const duration =
    execution && typeof execution["durationMs"] === "number"
      ? (execution["durationMs"] as number)
      : undefined;
  const result: ExecutionResult = { success: true };
  if (duration !== undefined) result.duration = duration;
  return {
    artifact: { ...innerArtifact(data["artifact"] as Record<string, unknown>), success: true },
    execution: result,
  };
}
